import { FC, useEffect } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";

import {
  Box,
  Button,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Delete } from "@mui/icons-material";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useDialogStore } from "../../../state/dialog-store";
import {
  adminPostVehicleTypeReqDtoSchema,
  useVehicleTypeControllerCreateVehicleType,
  useVehicleTypeControllerDeleteVehicleType,
  useVehicleTypeControllerGetVehicleType,
  useVehicleTypeControllerUpdateVehicleType,
} from "service";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";

export type AdminPostVehicleTypeDto = {
  typeName: string;
  travelSpeed: number;
};

const AddVehicleTypePage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const { typeId } = useParams<{
    typeId: string;
  }>();

  const { mutate, isLoading } = useVehicleTypeControllerCreateVehicleType({
    onSuccess: () => {
      toast.success("Vehicle Type Created Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicle-types",
          operationId: "vehicleTypeControllerFindMany",
          variables: {},
        }),
      );
      resetForm();
      setTimeout(() => {
        navigate(AdRoutes.vehicleTypes);
      }, 10);
    },
  });

  const { data: vehicleData, isLoading: loadingVehicle } =
    useVehicleTypeControllerGetVehicleType(
      {
        pathParams: {
          typeId: typeId ? parseInt(typeId) : 0,
        },
      },
      {
        enabled: !!typeId,
      },
    );

  const { mutate: updateVehicle } = useVehicleTypeControllerUpdateVehicleType({
    onSuccess: () => {
      toast.success("Vehicle Type Updated Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicle-types",
          operationId: "vehicleTypeControllerFindMany",
          variables: {},
        }),
      );
      resetForm();
      setTimeout(() => {
        navigate(AdRoutes.vehicleTypes);
      }, 10);
    },
  });

  const { mutate: removeVehicleType } =
    useVehicleTypeControllerDeleteVehicleType({
      onSuccess: () => {
        toast.success("Vehicle Deleted Successfully");
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/vehicle-types",
            operationId: "vehicleTypeControllerFindMany",
            variables: {},
          }),
        );
        setTimeout(() => {
          navigate(AdRoutes.vehicleTypes);
        }, 10);
      },
    });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    resetForm,
  } = useFormik<AdminPostVehicleTypeDto>({
    initialValues: {
      typeName: "",
      travelSpeed: 0,
    },
    validationSchema: toFormikValidationSchema(
      adminPostVehicleTypeReqDtoSchema,
    ),
    onSubmit: (values) => {
      if (!typeId) {
        return mutate({
          body: {
            travelSpeed: values.travelSpeed,
            typeName: values.typeName.trim().replace(/\s+/g, " "),
          },
        });
      }
      return updateVehicle({
        body: {
          travelSpeed: values.travelSpeed,
          typeName: values.typeName.trim().replace(/\s+/g, " "),
        },
        pathParams: {
          typeId: typeId ? parseInt(typeId) : 0,
        },
      });
    },
  });

  useUnsavedChangesBlocker(dirty);

  useEffect(() => {
    if (typeId && !loadingVehicle && vehicleData?.data) {
      resetForm({
        values: {
          typeName: vehicleData.data.typeName,
          travelSpeed: vehicleData.data.travelSpeed,
        },
      });
    }
  }, [vehicleData, loadingVehicle]);

  const onDeleteVehicle = () => {
    setDialog({
      title: "Delete Vehicle Type",
      description: `Are you sure you want to delete ${values?.typeName}? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        removeVehicleType({
          pathParams: { typeId: typeId ? parseInt(typeId) : 0 },
        });
        onClose();
      },
    });
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Fleet Management",
                url: AdRoutes.fleetManagement,
              },
              {
                label: typeId ? "Edit Vehicle Type" : "Add New Vehicle Type",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              date-testid="submit-vehicle-type-button"
            >
              {typeId ? "Save Changes" : "Add Vehicle Type"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.vehicleDetailContainer}>
        <Typography fontWeight={600}>Vehicle Type</Typography>
        <TextField
          id="typeName"
          type="text"
          value={values.typeName}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.typeName && errors.typeName}
          error={touched.typeName && !!errors.typeName}
          margin="dense"
          variant="outlined"
          fullWidth
        />
        <Typography fontWeight={600}>Travel Speed</Typography>
        <TextField
          id="travelSpeed"
          type="number"
          value={values.travelSpeed}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.travelSpeed && errors.travelSpeed}
          error={touched.travelSpeed && !!errors.travelSpeed}
          margin="dense"
          variant="outlined"
          InputProps={{
            endAdornment: <InputAdornment position="end">mph</InputAdornment>,
          }}
          fullWidth
        />
      </Box>
      {typeId && (
        <>
          <Divider sx={styles.divider} />
          <Box sx={styles.vehicleDetailContainer}>
            <Typography fontWeight={600} alignSelf="start">
              Delete Vehicle Type
            </Typography>
            <Box display="grid" gap="20px" alignItems="flex-start">
              <Typography variant="caption">
                Before deleting this vehicle type, please note that doing so
                will result in vehicles assigend to this vehicle type in being
                unassigned.
              </Typography>
              <Button
                color="error"
                variant="outlined"
                sx={styles.deleteButton}
                onClick={onDeleteVehicle}
                startIcon={<Delete />}
                data-testid="delete-vehicle-type"
              >
                Delete Vehicle Type
              </Button>
            </Box>
          </Box>
        </>
      )}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  vehicleDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  divider: {
    mt: "40px",
  },
}));

export default AddVehicleTypePage;
